import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "../../convex/_generated/api";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Edit,
  Trash2,
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  User,
  Calendar,
  UserCheck,
  UserX,
  AlertTriangle,
  X,
  ExternalLink,
  Copy,
} from "lucide-react";
import type { Id } from "../../convex/_generated/dataModel";

interface ContactTableProps {
  preloadedContacts?: any[];
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const QUALIFICATION_OPTIONS = [
  {
    value: "qualified",
    label: "Qualified",
    icon: UserCheck,
    color: "bg-green-100 text-green-800",
  },
  {
    value: "unqualified",
    label: "Unqualified",
    icon: UserX,
    color: "bg-gray-100 text-gray-800",
  },
  {
    value: "not approve",
    label: "Not Approved",
    icon: AlertTriangle,
    color: "bg-red-100 text-red-800",
  },
];

const TITLE_OPTIONS = [
  { value: "no_title", label: "No Title" },
  { value: "Mr.", label: "Mr." },
  { value: "Mrs.", label: "Mrs." },
];

// helper function to extract a google maps url embedded anywhere in the address text.
// returns only the url substring so surrounding text or trailing characters never
// reach google maps, which rejects mangled links with "unsupported link" on mobile
const extractGoogleMapsUrl = (address: string): string | null => {
  const match = address.match(
    /https?:\/\/(?:maps\.app\.goo\.gl|goo\.gl\/maps|maps\.google\.[a-z.]+|(?:www\.)?google\.[a-z.]+\/maps|plus\.codes)[^\s"'<>]*/i
  );
  if (!match) return null;
  return match[0];
};

// helper function to create google maps url
const createGoogleMapsUrl = (address: string, country: string) => {
  if (!address || address.trim() === "") return null;

  // if address contains a google maps url, link to it directly
  const mapsUrl = extractGoogleMapsUrl(address);
  if (mapsUrl) return mapsUrl;

  const query = encodeURIComponent(`${address}, ${country}`);
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
};

// helper function to detect goo.gl short links that need server-side resolution
const isMapsShortLink = (url: string) =>
  /^https?:\/\/(maps\.app\.goo\.gl|goo\.gl)\//i.test(url);

// short links resolved this session, so each one is only resolved once
const resolvedMapsUrlCache = new Map<string, string>();

// maps link that pre-resolves goo.gl short links into coordinate urls via the
// backend, because the ios google maps app rejects the "dropped pin" share urls
// those short links redirect to ("unsupported link")
function ContactMapsLink({
  address,
  country,
}: {
  address: string;
  country: string;
}) {
  const resolveShortLink = useAction(api.contacts.resolveMapsShortLink);
  const mapsUrl = extractGoogleMapsUrl(address);
  const shortLink =
    mapsUrl !== null && isMapsShortLink(mapsUrl) ? mapsUrl : null;
  const cachedUrl =
    shortLink !== null ? resolvedMapsUrlCache.get(shortLink) : undefined;
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(
    cachedUrl !== undefined ? cachedUrl : null
  );

  // resolve up front so the anchor already points at a coordinate url when
  // tapped -- resolving on click would break the ios universal link handoff
  useEffect(() => {
    if (shortLink === null || resolvedMapsUrlCache.has(shortLink)) return;
    let cancelled = false;
    resolveShortLink({ url: shortLink })
      .then((result) => {
        if (!result.ok) return;
        resolvedMapsUrlCache.set(shortLink, result.data);
        if (!cancelled) setResolvedUrl(result.data);
      })
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, [shortLink, resolveShortLink]);

  const href =
    resolvedUrl !== null ? resolvedUrl : createGoogleMapsUrl(address, country);
  if (href === null) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-auto p-0.5 text-left justify-start hover:bg-blue-50 hover:text-blue-700 active:scale-95 transition-all duration-150 ease-out cursor-pointer rounded-md text-xs"
      title={
        mapsUrl !== null
          ? "Open Google Maps link"
          : `Open ${address} in Google Maps`
      }
    >
      <div className="text-sm text-muted-foreground hover:text-blue-700 truncate max-w-32 flex items-center gap-1">
        {mapsUrl !== null ? "📍 Maps Link" : address}
        <ExternalLink className="h-3 w-3 opacity-60" />
      </div>
    </a>
  );
}

export function ContactTable({ preloadedContacts }: ContactTableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [dialogMode, setDialogMode] = useState<
    "view" | "edit" | "create" | "delete" | "duplicate" | null
  >(null);
  const [deleteContact, setDeleteContact] = useState<any>(null);

  // form state
  const [formData, setFormData] = useState({
    name: "",
    title: "no_title" as "no_title" | "Mr." | "Mrs.",
    phone: "",
    email: "",
    address: "",
    country: "",
    qualification: "unqualified" as "qualified" | "unqualified" | "not approve",
    property_id: "",
    reff_id: "",
    group_id: "" as Id<"contact_group"> | "",
    type_id: "" as Id<"contact_type"> | "",
    note: "",
    google_url: "",
    images: "",
  });

  // filters
  const [qualificationFilter, setQualificationFilter] = useState<string>("");
  const [countryFilter, setCountryFilter] = useState<string>("");
  const [groupFilter, setGroupFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");

  // pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // reset pagination when search term changes
  useEffect(() => {
    setCurrentPage(1);
    console.log("contacts searchterm:", searchTerm);
  }, [searchTerm, qualificationFilter, countryFilter, groupFilter, typeFilter]);

  // queries
  const isSearching = searchTerm.trim().length > 0;

  const allContacts = useQuery(
    api.contacts.getAllContacts,
    !isSearching
      ? {
          limit: 1000, // fetch all contacts for client-side pagination
          filters: {
            qualification:
              (qualificationFilter as
                | "qualified"
                | "unqualified"
                | "not approve") || undefined,
            country: countryFilter || undefined,
            group_id: (groupFilter as Id<"contact_group">) || undefined,
            type_id: (typeFilter as Id<"contact_type">) || undefined,
          },
        }
      : "skip"
  );

  const searchResults = useQuery(
    api.contacts.searchContacts,
    isSearching
      ? {
          searchTerm: searchTerm.trim(),
          limit: 1000,
        }
      : "skip"
  );

  const contactGroups = useQuery(api.contacts.getAllContactGroups, {});
  const contactTypes = useQuery(api.contacts.getAllContactTypes, {});
  const countries = useQuery(api.contacts.getUniqueCountries, {});
  const currentUser = useQuery(api.users.current);
  const properties = useQuery(
    api.properties.getAllBaseProperties,
    dialogMode !== null
      ? {
          filters: {},
        }
      : "skip"
  );

  // mutations
  const createContactMutation = useMutation(api.contacts.createContact);
  const updateContactMutation = useMutation(api.contacts.updateContact);
  const deleteContactMutation = useMutation(api.contacts.deleteContact);

  // derive contacts data
  const contacts = useMemo(() => {
    if (isSearching) {
      // when searching, only use search results (don't fall back to preloaded)
      return searchResults || [];
    }

    // when not searching, prefer fresh data over preloaded if available
    if (allContacts && allContacts.length > 0) return allContacts;
    if (preloadedContacts) return preloadedContacts;

    return [];
  }, [preloadedContacts, allContacts, searchResults, isSearching]);

  // apply client-side filters for non-search mode
  const filteredContacts = useMemo(() => {
    if (isSearching) return contacts; // search already filters server-side

    return contacts.filter((contact) => {
      if (qualificationFilter && contact.qualification !== qualificationFilter)
        return false;
      if (countryFilter && contact.country !== countryFilter) return false;
      if (groupFilter && contact.group_id !== groupFilter) return false;
      if (typeFilter && contact.type_id !== typeFilter) return false;
      return true;
    });
  }, [
    contacts,
    qualificationFilter,
    countryFilter,
    groupFilter,
    typeFilter,
    isSearching,
  ]);

  // client-side pagination
  const paginatedContacts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredContacts.slice(startIndex, endIndex);
  }, [filteredContacts, currentPage, pageSize]);

  // pagination info
  const paginationInfo = useMemo(() => {
    const totalCount = filteredContacts.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const showingStart = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const showingEnd = Math.min(currentPage * pageSize, totalCount);

    return {
      totalPages,
      totalCount,
      showingStart,
      showingEnd,
      canGoPrevious: currentPage > 1,
      canGoNext: currentPage < totalPages,
    };
  }, [filteredContacts.length, pageSize, currentPage]);

  // helper functions
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleDateString();
  };

  const formatPhone = (phone: number) => {
    return phone.toString().replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
  };

  const getQualificationBadge = (qualification: string) => {
    const option = QUALIFICATION_OPTIONS.find(
      (opt) => opt.value === qualification
    );
    if (!option) return null;

    const Icon = option.icon;
    return (
      <Badge className={`${option.color} text-xs`}>
        <Icon className="h-3 w-3 mr-1" />
        {option.label}
      </Badge>
    );
  };

  const getContactGroupName = (groupId: Id<"contact_group"> | null) => {
    if (!groupId || !contactGroups) return "N/A";
    const group = contactGroups.find((g) => g._id === groupId);
    return group?.group_name || "N/A";
  };

  const getContactTypeName = (typeId: Id<"contact_type"> | null) => {
    if (!typeId || !contactTypes) return "N/A";
    const type = contactTypes.find((t) => t._id === typeId);
    return type?.type_name || "N/A";
  };

  // event handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: string) => {
    setPageSize(Number(newPageSize));
    setCurrentPage(1);
  };

  const handlePrevious = () => {
    if (paginationInfo.canGoPrevious) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (paginationInfo.canGoNext) {
      handlePageChange(currentPage + 1);
    }
  };

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const totalPages = paginationInfo.totalPages;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const resetForm = () => {
    setFormData({
      name: "",
      title: "no_title",
      phone: "",
      email: "",
      address: "",
      country: "",
      qualification: "unqualified",
      property_id: "",
      reff_id: "",
      group_id: "",
      type_id: "",
      note: "",
      google_url: "",
      images: "",
    });
  };

  const handleOpenDialog = (
    mode: "view" | "edit" | "create" | "duplicate",
    contact?: any
  ) => {
    setDialogMode(mode);
    setSelectedContact(contact);

    if (mode === "create") {
      resetForm();
    } else if (contact) {
      setFormData({
        name: contact.name || "",
        title: contact.title || "no_title",
        phone: contact.phone?.toString() || "",
        email: contact.email || "",
        address: contact.address || "",
        country: contact.country || "",
        qualification: contact.qualification || "unqualified",
        property_id:
          mode === "duplicate" ? "" : contact.property_id?.toString() || "",
        reff_id: contact.reff_id || "",
        group_id: contact.group_id || "",
        type_id: contact.type_id || "",
        note: contact.note || "",
        google_url: contact.google_url || "",
        images: contact.images || "",
      });
    }
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    setSelectedContact(null);
    setDeleteContact(null);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!currentUser) {
      toast.error("You must be logged in to perform this action");
      return;
    }

    try {
      const phoneNumber = parseInt(formData.phone);
      if (isNaN(phoneNumber)) {
        toast.error("Phone number must be a valid number");
        return;
      }

      const contactData = {
        name: formData.name || undefined,
        title: formData.title,
        phone: phoneNumber,
        email: formData.email || undefined,
        address: formData.address || undefined,
        country: formData.country,
        qualification: formData.qualification,
        property_id: formData.property_id
          ? parseInt(formData.property_id)
          : undefined,
        reff_id: formData.reff_id || undefined,
        group_id: (formData.group_id as Id<"contact_group">) || undefined,
        type_id: (formData.type_id as Id<"contact_type">) || undefined,
        note: formData.note || undefined,
        google_url: formData.google_url || undefined,
        images: formData.images || undefined,
      };

      if (dialogMode === "create" || dialogMode === "duplicate") {
        if (dialogMode === "duplicate" && !formData.property_id) {
          toast.error("Please select a property for the duplicated contact");
          return;
        }
        await createContactMutation({
          ...contactData,
          created_by: currentUser.email || currentUser.name || "Unknown",
        });
        toast.success(
          dialogMode === "duplicate"
            ? "Contact duplicated successfully"
            : "Contact created successfully"
        );
      } else if (dialogMode === "edit" && selectedContact) {
        await updateContactMutation({
          id: selectedContact._id,
          ...contactData,
          updated_by: currentUser.email || currentUser.name || "Unknown",
        });
        toast.success("Contact updated successfully");
      }

      handleCloseDialog();
    } catch (error) {
      console.error("Contact operation failed:", error);
      toast.error("Failed to save contact");
    }
  };

  const handleDelete = async () => {
    if (!deleteContact || !currentUser) return;

    try {
      await deleteContactMutation({
        id: deleteContact._id,
        deleted_by: currentUser.email || currentUser.name || "Unknown",
      });
      toast.success("Contact deleted successfully");
      handleCloseDialog();
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete contact");
    }
  };

  const clearFilters = () => {
    setQualificationFilter("");
    setCountryFilter("");
    setGroupFilter("");
    setTypeFilter("");
    setSearchTerm("");
  };

  return (
    <div className="w-full space-y-3">
      <div className="w-full">
        <div className="px-3 pt-3 pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm">Contact Management</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Showing {paginationInfo.showingStart}-
                {paginationInfo.showingEnd} of {paginationInfo.totalCount}{" "}
                contacts
              </p>
            </div>
            <Button
              onClick={() => handleOpenDialog("create")}
              size="sm"
              className="h-8 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Contact
            </Button>
          </div>
        </div>
        <div className="px-3 pb-3">
          {/* search and filters */}
          <div className="flex flex-col gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search contacts by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-8 text-xs"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div className="relative">
                <Select
                  value={qualificationFilter || undefined}
                  onValueChange={(value) => setQualificationFilter(value || "")}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by qualification" />
                  </SelectTrigger>
                  <SelectContent>
                    {QUALIFICATION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {qualificationFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setQualificationFilter("")}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 bg-background border"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>

              <div className="relative">
                <Select
                  value={countryFilter || undefined}
                  onValueChange={(value) => setCountryFilter(value || "")}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries?.map((country) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {countryFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCountryFilter("")}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 bg-background border"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>

              <div className="relative">
                <Select
                  value={groupFilter || undefined}
                  onValueChange={(value) => setGroupFilter(value || "")}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by group" />
                  </SelectTrigger>
                  <SelectContent>
                    {contactGroups?.map((group) => (
                      <SelectItem key={group._id} value={group._id}>
                        {group.group_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {groupFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setGroupFilter("")}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 bg-background border"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>

              <div className="relative">
                <Select
                  value={typeFilter || undefined}
                  onValueChange={(value) => setTypeFilter(value || "")}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    {contactTypes?.map((type) => (
                      <SelectItem key={type._id} value={type._id}>
                        {type.type_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {typeFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setTypeFilter("")}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 bg-background border"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>

            {(qualificationFilter ||
              countryFilter ||
              groupFilter ||
              typeFilter ||
              searchTerm) && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">
                  Filters applied:
                </span>
                <Button variant="outline" size="sm" onClick={clearFilters} className="h-7 text-xs active:scale-95 transition-all duration-150 ease-out cursor-pointer">
                  Clear all filters
                </Button>
              </div>
            )}
          </div>

          {/* page size selector */}
          <div className="flex items-center gap-1.5 mb-2">
            <span className="text-xs text-muted-foreground">Show</span>
            <Select
              value={pageSize.toString()}
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger className="w-16 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">per page</span>
          </div>

          {/* contacts table */}
          <div className="rounded-md border max-h-[calc(100vh-300px)] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Phone & Email</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Qualification</TableHead>
                  <TableHead>Group & Type</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedContacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-6">
                      <User className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm font-medium mb-1">
                        No contacts found
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {searchTerm ||
                        qualificationFilter ||
                        countryFilter ||
                        groupFilter ||
                        typeFilter
                          ? "Try adjusting your search or filters"
                          : "Get started by adding your first contact"}
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedContacts.map((contact) => (
                    <TableRow key={contact._id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                            <User className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-xs truncate">
                              {contact.title !== "no_title" &&
                                `${contact.title} `}
                              {contact.name || "N/A"}
                            </div>
                            {contact.reff_id && (
                              <div className="text-xs text-muted-foreground truncate">
                                Ref: {contact.reff_id}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1.5">
                            <Phone className="h-3 w-3 text-muted-foreground shrink-0" />
                            <span className="text-xs truncate">
                              {formatPhone(contact.phone)}
                            </span>
                          </div>
                          {contact.email && contact.email !== "N/A" && (
                            <div className="flex items-center gap-1.5">
                              <Mail className="h-3 w-3 text-muted-foreground shrink-0" />
                              <span className="text-xs truncate">{contact.email}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-xs truncate">{contact.country}</div>
                            {contact.address && (
                              <ContactMapsLink
                                address={contact.address}
                                country={contact.country}
                              />
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getQualificationBadge(contact.qualification)}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-0.5">
                          <div className="text-xs font-medium truncate">
                            {getContactGroupName(contact.group_id)}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {getContactTypeName(contact.type_id)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3 w-3 text-muted-foreground shrink-0" />
                          <span className="text-xs">
                            {formatDate(contact.created_at)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog("view", contact)}
                            className="h-7 w-7 p-0 text-xs active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                          >
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleOpenDialog("duplicate", contact)
                            }
                            title="Duplicate contact"
                            className="h-7 w-7 p-0 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog("edit", contact)}
                            className="h-7 w-7 p-0 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeleteContact(contact);
                              setDialogMode("delete");
                            }}
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* pagination controls */}
          {paginatedContacts.length > 0 && paginationInfo.totalPages > 1 && (
            <div className="mt-2">
              <Pagination>
                <PaginationContent className="gap-1">
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={handlePrevious}
                      className={
                        !paginationInfo.canGoPrevious
                          ? "pointer-events-none opacity-50 h-7"
                          : "cursor-pointer h-7"
                      }
                    />
                  </PaginationItem>

                  {generatePageNumbers().map((page, index) => (
                    <PaginationItem key={index}>
                      {page === "ellipsis" ? (
                        <PaginationEllipsis className="h-7" />
                      ) : (
                        <PaginationLink
                          onClick={() => handlePageChange(page as number)}
                          isActive={currentPage === page}
                          className="cursor-pointer h-7 w-7 text-xs"
                        >
                          {page}
                        </PaginationLink>
                      )}
                    </PaginationItem>
                  ))}

                  <PaginationItem>
                    <PaginationNext
                      onClick={handleNext}
                      className={
                        !paginationInfo.canGoNext
                          ? "pointer-events-none opacity-50 h-7"
                          : "cursor-pointer h-7"
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>

              <div className="text-center mt-1.5 text-xs text-muted-foreground">
                Page {currentPage} of {paginationInfo.totalPages}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* contact dialog */}
      {dialogMode && dialogMode !== "delete" && (
        <Dialog open={!!dialogMode} onOpenChange={handleCloseDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {dialogMode === "create" && "Create New Contact"}
                {dialogMode === "edit" && "Edit Contact"}
                {dialogMode === "view" && "Contact Details"}
                {dialogMode === "duplicate" && "Duplicate Contact"}
              </DialogTitle>
              <DialogDescription>
                {dialogMode === "create" &&
                  "Add a new contact to your database."}
                {dialogMode === "edit" && "Update the contact information."}
                {dialogMode === "view" &&
                  "View contact details and information."}
                {dialogMode === "duplicate" &&
                  "Duplicate this contact and assign it to a different property."}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="title" className="text-right">
                  Title
                </Label>
                <Select
                  value={formData.title}
                  onValueChange={(value: "no_title" | "Mr." | "Mrs.") =>
                    setFormData({ ...formData, title: value })
                  }
                  disabled={dialogMode === "view"}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TITLE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Name
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="col-span-3"
                  disabled={dialogMode === "view"}
                />
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="phone" className="text-right">
                  Phone *
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="col-span-3"
                  placeholder="e.g. 1234567890"
                  disabled={dialogMode === "view"}
                  required
                />
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="email" className="text-right">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="col-span-3"
                  disabled={dialogMode === "view"}
                />
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="country" className="text-right">
                  Country *
                </Label>
                <Select
                  value={formData.country || undefined}
                  onValueChange={(value) =>
                    setFormData({ ...formData, country: value || "" })
                  }
                  disabled={dialogMode === "view"}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries?.map((country) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="address" className="text-right">
                  Address
                </Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  className="col-span-3"
                  disabled={dialogMode === "view"}
                />
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="qualification" className="text-right">
                  Qualification
                </Label>
                <Select
                  value={formData.qualification}
                  onValueChange={(
                    value: "qualified" | "unqualified" | "not approve"
                  ) => setFormData({ ...formData, qualification: value })}
                  disabled={dialogMode === "view"}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUALIFICATION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="group_id" className="text-right">
                  Group
                </Label>
                <div className="relative col-span-3">
                  <Select
                    value={formData.group_id || undefined}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        group_id: (value || "") as Id<"contact_group"> | "",
                      })
                    }
                    disabled={dialogMode === "view"}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select group" />
                    </SelectTrigger>
                    <SelectContent>
                      {contactGroups?.map((group) => (
                        <SelectItem key={group._id} value={group._id}>
                          {group.group_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.group_id && dialogMode !== "view" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({ ...formData, group_id: "" })}
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 bg-background border"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="type_id" className="text-right">
                  Type
                </Label>
                <div className="relative col-span-3">
                  <Select
                    value={formData.type_id || undefined}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        type_id: (value || "") as Id<"contact_type"> | "",
                      })
                    }
                    disabled={dialogMode === "view"}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {contactTypes?.map((type) => (
                        <SelectItem key={type._id} value={type._id}>
                          {type.type_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.type_id && dialogMode !== "view" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({ ...formData, type_id: "" })}
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 bg-background border"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="property_id" className="text-right">
                  Property
                </Label>
                {dialogMode === "view" ? (
                  <div className="col-span-3">
                    {formData.property_id ? (
                      (() => {
                        const property = properties?.find(
                          (p) => p.id.toString() === formData.property_id
                        );
                        return property ? (
                          <div className="flex flex-col py-2 px-3 rounded-md border bg-muted/50">
                            <span className="font-medium text-sm">
                              {property.property_name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {property.reff_id}
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col py-2 px-3 rounded-md border bg-muted/50">
                            <span className="text-xs text-muted-foreground">
                              Property not found
                            </span>
                          </div>
                        );
                      })()
                    ) : (
                      <div className="py-2 px-3 rounded-md border bg-muted/50 text-sm text-muted-foreground">
                        No property assigned
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="col-span-3">
                    <SearchableSelect
                      value={formData.property_id || undefined}
                      onValueChange={(value) =>
                        setFormData({ ...formData, property_id: value || "" })
                      }
                      placeholder="Select a property"
                      searchPlaceholder="Search by property name or reference ID..."
                      emptyText="No properties found"
                      disabled={false}
                      options={
                        properties?.map((property) => ({
                          value: property.id.toString(),
                          label: property.property_name,
                          description: `ID: ${property.reff_id}`,
                        })) || []
                      }
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="reff_id" className="text-right">
                  Reference ID
                </Label>
                <Input
                  id="reff_id"
                  value={formData.reff_id}
                  onChange={(e) =>
                    setFormData({ ...formData, reff_id: e.target.value })
                  }
                  className="col-span-3"
                  disabled={dialogMode === "view"}
                />
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="google_url" className="text-right">
                  Google URL
                </Label>
                <Input
                  id="google_url"
                  type="url"
                  value={formData.google_url}
                  onChange={(e) =>
                    setFormData({ ...formData, google_url: e.target.value })
                  }
                  className="col-span-3"
                  disabled={dialogMode === "view"}
                />
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="images" className="text-right">
                  Images
                </Label>
                <Input
                  id="images"
                  value={formData.images}
                  onChange={(e) =>
                    setFormData({ ...formData, images: e.target.value })
                  }
                  className="col-span-3"
                  placeholder="Image URLs or references"
                  disabled={dialogMode === "view"}
                />
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="note" className="text-right">
                  Note
                </Label>
                <Textarea
                  id="note"
                  value={formData.note}
                  onChange={(e) =>
                    setFormData({ ...formData, note: e.target.value })
                  }
                  className="col-span-3"
                  disabled={dialogMode === "view"}
                />
              </div>
            </div>

            {dialogMode !== "view" && (
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseDialog}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={dialogMode === "duplicate" && !formData.property_id}
                >
                  {dialogMode === "create" && "Create Contact"}
                  {dialogMode === "edit" && "Save Changes"}
                  {dialogMode === "duplicate" && "Duplicate Contact"}
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* delete confirmation dialog */}
      {dialogMode === "delete" && deleteContact && (
        <Dialog open={dialogMode === "delete"} onOpenChange={handleCloseDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Contact</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "
                {deleteContact.name || "this contact"}"? This action cannot be
                undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete Contact
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
